import { BrandEmblem } from "./BrandEmblem";
import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Loader2 } from "lucide-react";

const fInput =
  "w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/20 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37] transition-colors";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      const { data } = await api.post("/contact", form);
      setStatus(`Inquiry received (${data.reference_id}). Our team will respond within 1–2 business days.`);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus(`Error: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="footer-contact-form">
      <input className={fInput} placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
      <input className={fInput} type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
      <textarea className={`${fInput} min-h-[80px]`} placeholder="How can we help?" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
      <button type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold py-2.5 rounded-lg hover:bg-[#e8c968] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="contact-submit-button">
        {busy && <Loader2 size={14} className="animate-spin" />} Send Inquiry
      </button>
      {status && <p className={`text-xs ${status.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`} data-testid="contact-status">{status}</p>}
    </form>
  );
};

export const Footer = ({ onOpenAbout }) => (
  <footer className="bg-[#0B1B2B] border-t border-[#D4AF37]/20" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <div className="flex items-center gap-3">
          <BrandEmblem />
          <div>
            <div className="font-serif text-lg text-white font-semibold">Acuity Rise Education Group LLC</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Clinical Education Infrastructure</div>
          </div>
        </div>
        <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-sm">
          Enterprise-grade placement infrastructure and workforce development — architecting reliable pathways to clinical excellence for the institutions training the healthcare workforce.
        </p>
      </div>
      <div className="lg:col-span-3 mt-10 lg:mt-0">
        <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Navigate</div>
        <div className="mt-5 space-y-3">
          {[["Executive Overview", "#hero"], ["Stakeholder Pathways", "#stakeholder-grid"]].map(([l, t]) => (
            <button key={l} onClick={() => document.querySelector(t)?.scrollIntoView({ behavior: "smooth" })} className="block text-sm text-slate-300 hover:text-white gold-underline">
              {l}
            </button>
          ))}
          <button onClick={onOpenAbout} className="block text-sm text-slate-300 hover:text-white gold-underline" data-testid="footer-about-link">About Us</button>
          <a href="/sunset-lake" className="block text-sm text-slate-300 hover:text-white gold-underline" data-testid="footer-sunset-lake-link">Sunset Lake Campus</a>
        </div>
      </div>
      <div className="lg:col-span-4 mt-10 lg:mt-0">
        <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Contact Us</div>
        <ContactForm />
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
        <span>© {new Date().getFullYear()} Acuity Rise Education Group LLC. All rights reserved.</span>
        <span>Confidential submissions · Encrypted intake vault</span>
      </div>
    </div>
  </footer>
);
